import { useState, useEffect, useCallback } from "react";
import { API, authFetch } from "@/App";
import {
  MapPin, Battery, Clock, Navigation, RefreshCw, User,
  Circle, Phone, Wifi, WifiOff,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const FieldTrackingMapPage = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [playbackData, setPlaybackData] = useState(null);
  const [playbackDate, setPlaybackDate] = useState(new Date().toISOString().split("T")[0]);

  const fetchLiveStatus = useCallback(async () => {
    const res = await authFetch(`${API}/field-tracking/live-status`);
    const data = await res.json();
    if (data.success) setEmployees(data.data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchLiveStatus(); }, [fetchLiveStatus]);
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchLiveStatus, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchLiveStatus]);

  const fetchPlayback = async (empId) => {
    const res = await authFetch(`${API}/field-tracking/${empId}/route-playback?date=${playbackDate}`);
    const data = await res.json();
    if (data.success) setPlaybackData(data.data);
  };

  const filtered = employees.filter(e =>
    !search || e.employee_name?.toLowerCase().includes(search.toLowerCase()) || e.employee_id?.toLowerCase().includes(search.toLowerCase())
  );

  const online = filtered.filter(e => e.is_online);
  const offline = filtered.filter(e => !e.is_online);
  const totalHours = filtered.reduce((s, e) => s + (e.hours_on_field || 0), 0);

  const batteryColor = (level) => {
    if (!level && level !== 0) return "text-slate-400";
    if (level > 60) return "text-emerald-500";
    if (level > 20) return "text-amber-500";
    return "text-red-500";
  };

  return (
    <div className="space-y-5" data-testid="field-tracking-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white font-['Sora']">Field Force Tracking</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Real-time location, battery & route playback</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant={autoRefresh ? "default" : "outline"} onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""} data-testid="auto-refresh-toggle">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${autoRefresh ? "animate-spin" : ""}`} style={autoRefresh ? { animationDuration: "3s" } : {}} />
            {autoRefresh ? "Live" : "Paused"}
          </Button>
          <Button size="sm" variant="outline" onClick={fetchLiveStatus} data-testid="refresh-btn">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-3 dark:bg-slate-800">
          <div className="flex items-center gap-2"><Circle className="w-2.5 h-2.5 text-emerald-500 fill-emerald-500" /><span className="text-xs text-slate-500 dark:text-slate-400">Online</span></div>
          <p className="text-2xl font-bold text-emerald-600 font-mono mt-1">{online.length}</p>
        </Card>
        <Card className="p-3 dark:bg-slate-800">
          <div className="flex items-center gap-2"><Circle className="w-2.5 h-2.5 text-slate-400 fill-slate-400" /><span className="text-xs text-slate-500 dark:text-slate-400">Offline</span></div>
          <p className="text-2xl font-bold text-slate-500 font-mono mt-1">{offline.length}</p>
        </Card>
        <Card className="p-3 dark:bg-slate-800">
          <div className="flex items-center gap-2"><Clock className="w-3 h-3 text-blue-500" /><span className="text-xs text-slate-500 dark:text-slate-400">Total Field Hours</span></div>
          <p className="text-2xl font-bold text-blue-600 font-mono mt-1">{totalHours.toFixed(1)}h</p>
        </Card>
        <Card className="p-3 dark:bg-slate-800">
          <div className="flex items-center gap-2"><User className="w-3 h-3 text-purple-500" /><span className="text-xs text-slate-500 dark:text-slate-400">Total Tracked</span></div>
          <p className="text-2xl font-bold text-purple-600 font-mono mt-1">{filtered.length}</p>
        </Card>
      </div>

      {/* Search */}
      <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or ID..." className="max-w-xs" data-testid="field-search" />

      {/* Employee Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {loading ? (
          <div className="col-span-full text-center py-12"><div className="w-6 h-6 spinner mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <MapPin className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500 dark:text-slate-400">No employees checked in today</p>
            <p className="text-xs text-slate-400 mt-1">Field staff will appear here once they punch in from the mobile app</p>
          </div>
        ) : filtered.map(emp => (
          <Card key={emp.employee_id} className={`p-4 dark:bg-slate-800 cursor-pointer hover:shadow-md transition border-l-4 ${emp.is_online ? "border-l-emerald-500" : "border-l-slate-300"}`}
            onClick={() => { setSelectedEmp(emp); fetchPlayback(emp.employee_id); }} data-testid={`emp-card-${emp.employee_id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${emp.is_online ? "bg-emerald-100" : "bg-slate-100"}`}>
                  <User className={`w-5 h-5 ${emp.is_online ? "text-emerald-600" : "text-slate-400"}`} />
                </div>
                <div>
                  <p className="font-semibold text-sm text-slate-900 dark:text-white">{emp.employee_name}</p>
                  <p className="text-[10px] text-slate-400">{emp.employee_id} {emp.role && `· ${emp.role}`}</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {emp.is_online ? <Wifi className="w-3.5 h-3.5 text-emerald-500" /> : <WifiOff className="w-3.5 h-3.5 text-slate-400" />}
                <Badge className={`text-[10px] ${emp.is_online ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  {emp.is_online ? "Online" : "Offline"}
                </Badge>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3 text-center">
              <div>
                <p className="text-[10px] text-slate-400">Check-in</p>
                <p className="text-xs font-mono font-medium text-slate-700 dark:text-slate-300">{emp.check_in_time ? new Date(emp.check_in_time).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—"}</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-400">Hours</p>
                <p className="text-xs font-mono font-medium text-blue-600">{(emp.hours_on_field || 0).toFixed(1)}h</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-400">Battery</p>
                <p className={`text-xs font-mono font-medium ${batteryColor(emp.last_battery)}`}>
                  <Battery className="w-3 h-3 inline mr-0.5" />{emp.last_battery != null ? `${emp.last_battery}%` : "—"}
                </p>
              </div>
            </div>
            {emp.last_location && (
              <p className="text-[10px] text-slate-400 mt-2 flex items-center gap-1">
                <Navigation className="w-2.5 h-2.5" />
                {emp.last_location.lat?.toFixed(4)}, {emp.last_location.lng?.toFixed(4)}
                {emp.last_seen && <span className="ml-auto">{new Date(emp.last_seen).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>}
              </p>
            )}
          </Card>
        ))}
      </div>

      {/* Route Playback Dialog */}
      <Dialog open={!!selectedEmp} onOpenChange={() => { setSelectedEmp(null); setPlaybackData(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="playback-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Navigation className="w-4 h-4 text-[#8B1A1A]" />
              Route Playback: {selectedEmp?.employee_name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Input type="date" value={playbackDate} onChange={e => setPlaybackDate(e.target.value)} className="w-44" data-testid="playback-date" />
              <Button size="sm" onClick={() => selectedEmp && fetchPlayback(selectedEmp.employee_id)} className="bg-[#8B1A1A] text-white" data-testid="load-playback">
                Load Route
              </Button>
            </div>

            {playbackData && (
              <>
                {/* Stats Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Card className="p-3 dark:bg-slate-800 text-center">
                    <p className="text-[10px] text-slate-400 uppercase">Distance</p>
                    <p className="text-lg font-bold font-mono text-[#8B1A1A]">{playbackData.stats.total_distance_km} km</p>
                  </Card>
                  <Card className="p-3 dark:bg-slate-800 text-center">
                    <p className="text-[10px] text-slate-400 uppercase">Field Hours</p>
                    <p className="text-lg font-bold font-mono text-blue-600">{playbackData.stats.hours_on_field}h</p>
                  </Card>
                  <Card className="p-3 dark:bg-slate-800 text-center">
                    <p className="text-[10px] text-slate-400 uppercase">GPS Pings</p>
                    <p className="text-lg font-bold font-mono text-purple-600">{playbackData.stats.total_pings}</p>
                  </Card>
                  <Card className="p-3 dark:bg-slate-800 text-center">
                    <p className="text-[10px] text-slate-400 uppercase">Visits</p>
                    <p className="text-lg font-bold font-mono text-emerald-600">{playbackData.stats.dealer_visits}</p>
                  </Card>
                </div>

                {/* TADA Breakdown */}
                <Card className="p-4 dark:bg-slate-800 border-l-4 border-l-amber-500">
                  <h4 className="font-semibold text-sm text-slate-900 dark:text-white mb-2">TADA Calculation</h4>
                  <div className="grid grid-cols-4 gap-3 text-center">
                    <div>
                      <p className="text-[10px] text-slate-400">Travel</p>
                      <p className="text-sm font-mono font-bold text-slate-700 dark:text-slate-300">Rs {playbackData.tada.travel_allowance}</p>
                      <p className="text-[9px] text-slate-400">{playbackData.tada.distance_km}km x Rs{playbackData.tada.rate_per_km}/km</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400">DA</p>
                      <p className="text-sm font-mono font-bold text-slate-700 dark:text-slate-300">Rs {playbackData.tada.daily_allowance}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400">Food</p>
                      <p className="text-sm font-mono font-bold text-slate-700 dark:text-slate-300">Rs {playbackData.tada.food_allowance}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400">Total TADA</p>
                      <p className="text-sm font-mono font-bold text-amber-600">Rs {playbackData.tada.total}</p>
                    </div>
                  </div>
                </Card>

                {/* Stoppages */}
                {playbackData.stoppages.length > 0 && (
                  <Card className="p-4 dark:bg-slate-800">
                    <h4 className="font-semibold text-sm text-slate-900 dark:text-white mb-2">Stoppages ({playbackData.stoppages.length})</h4>
                    <div className="space-y-2">
                      {playbackData.stoppages.map((s, i) => (
                        <div key={i} className="flex items-center gap-3 text-xs bg-slate-50 dark:bg-slate-900 rounded p-2">
                          <Circle className="w-2.5 h-2.5 text-red-500 fill-red-500 shrink-0" />
                          <div className="flex-1">
                            <p className="font-mono text-slate-600 dark:text-slate-400">{s.lat?.toFixed(4)}, {s.lng?.toFixed(4)}</p>
                          </div>
                          <Badge variant="outline" className="text-[10px]">{s.duration_mins} min</Badge>
                          <span className="text-[10px] text-slate-400">{new Date(s.start).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })} - {new Date(s.end).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

                {/* Battery Info */}
                <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
                  <span className="flex items-center gap-1"><Battery className="w-3 h-3" /> Last: {playbackData.stats.last_battery ?? "N/A"}%</span>
                  <span className="flex items-center gap-1"><Battery className="w-3 h-3 text-red-400" /> Min: {playbackData.stats.min_battery ?? "N/A"}%</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> In: {playbackData.stats.check_in_time ? new Date(playbackData.stats.check_in_time).toLocaleTimeString() : "—"}</span>
                  <span>Out: {playbackData.stats.check_out_time ? new Date(playbackData.stats.check_out_time).toLocaleTimeString() : "—"}</span>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FieldTrackingMapPage;
